import { useRef } from 'react';
import { useForm, useList, SubmitResult } from '@shopify/react-form';
import useSlingApi from '@sdk/api/useSlingApi';
import getErrorMessages from '@sdk/utils/getErrorMessages';
import type { LocationType } from '@sdk/locations/types/LocationType';

import { FormSubmitStatuses } from '@web/forms/types';
import { decorateSubmit } from '@web/forms/utils/decorateSubmit';

import type { LocationsFormType } from '../types/LocationsForm';
import type { EditPersonaCollectionGroup } from '../../onboardingChecklist.types';
import { getLocationsPayloads } from '../helpers/getLocationsPayloads';

type Props = {
  locations: LocationType[];
  close: () => void;
  editPersonaCollectionGroup: EditPersonaCollectionGroup;
};

export const useLocationsForm = ({ locations, close, editPersonaCollectionGroup }: Props) => {
  const api = useSlingApi();

  const locationsField: LocationsFormType[] = useList({
    list: locations.map((location) => ({
      id: location.id,
      address: '',
      name: location.name,
      coordinates: '',
    })),
  });

  const submitPromise = useRef<Promise<SubmitResult>>();
  const {
    submit: formSubmit,
    dirty,
    reset,
    submitErrors,
  } = useForm({
    fields: { locationsField },
    onSubmit: async () => {
      const updatedLocations = locationsField.filter(
        (item) => item.address.defaultValue !== item.address.value
      );
      const locationsPayloads = getLocationsPayloads(locations, updatedLocations);

      try {
        const responses = await Promise.all(locationsPayloads.map(api.locations.updateLocation));
        responses
          .map(({ data }) => data)
          .forEach((location) => editPersonaCollectionGroup({ group: location }));

        submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
        close();
        return { status: FormSubmitStatuses.success };
      } catch (error) {
        const [errorMsg] = getErrorMessages(error);

        submitPromise.current = Promise.resolve({
          status: FormSubmitStatuses.fail,
          errors: [{ message: errorMsg }],
        });
        return submitPromise.current;
      }
    },
  });

  const submit = decorateSubmit(formSubmit, submitPromise);
  return { locationsField, submit, dirty, reset, submitErrors };
};
